<template>
  <div class="dronecast-player" :class="{ compact: props.compact }">
    <video ref="remoteVideo" autoplay muted playsinline class="remote-video"></video>
    <canvas ref="detectionCanvas" class="detection-overlay"></canvas>

    <!-- 상태별 오버레이 -->
    <div class="overlay" v-if="connectionState !== 'playing'">
      <!-- 로딩 상태 (connecting, joining, subscribing, buffering) -->
      <div v-if="isLoading" class="overlay-content">
        <IconLoading class="loading-icon" :animate="true" />
        <p class="status-text" v-if="connectionState === 'joining'">
          캐스트룸 참여 중... ({{ retryCount }}/{{ maxRetry }})
        </p>
        <p class="status-text" v-else-if="connectionState === 'connecting'">서버에 연결 중...</p>
        <p class="status-text" v-else-if="connectionState === 'subscribing'">스트림 구독 중...</p>
        <p class="status-text" v-else-if="connectionState === 'buffering'">영상 버퍼링 중...</p>
      </div>

      <!-- 에러 상태 -->
      <div v-else-if="connectionState === 'error'" class="overlay-content error">
        <p class="error-message">{{ errorMessage }}</p>
        <button v-if="canRetry" class="retry-button" @click="retry">재시도</button>
      </div>

      <!-- 정지 상태 -->
      <div v-else-if="connectionState === 'stopped'" class="overlay-content stopped">
        <p class="stopped-message" v-if="stopReason === 'user'">재생이 중지되었습니다.</p>
        <p class="stopped-message" v-else-if="stopReason === 'server'">송출이 종료되었습니다.</p>
        <p class="stopped-message" v-else>연결이 종료되었습니다.</p>
      </div>

      <!-- idle 상태 -->
      <div v-else-if="connectionState === 'idle'" class="overlay-content idle">
        <p class="idle-message">스트림을 선택하세요.</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { DronecastClient } from '../core/dronecastClient'
import {
  DRONECAST_PROTOCOL,
  IDCMessage,
  TDCCastroomId,
  TDCPayloadCastroomStatusChanged,
  TDCPayloadExchangeIcecandidate,
  TDCPayloadJoinCastroomResult,
  TDCPayloadSubscribeStreamResult,
} from '../core/clientInterfaces'
import {
  type ConnectionState,
  type StopReason,
  type PlayerErrorCode,
  getErrorInfo,
} from '../core/errorMessages'
import { type PlayerWebRtcStats, createEmptyStats } from '../core/playerStats.types'
import IconLoading from '../icons/IconLoading.vue'
import { useObjectDetection } from '@/composables/useObjectDetection'
import { monitoringService } from '@/services/monitoring.service'

const props = defineProps<{
  castroomId: TDCCastroomId
  wsUrl: string
  autoPlay?: boolean
  compact?: boolean
  detectionEnabled?: boolean
}>()

const emit = defineEmits<{
  (e: 'onCastroomStatusChanged', result: { castroomId: any; status: string; reason?: 'caster_stopped' | 'admin_closed' | 'error' }): void
  (e: 'onConnectionStateChanged', state: ConnectionState): void
  (e: 'onWsStateChanged', state: 'connecting' | 'connected' | 'reconnecting' | 'disconnected'): void
  (e: 'onWebRtcStateChanged', role: 'viewer', connectionState?: string, iceState?: string): void
  (e: 'onCasterLinkStatusChanged', status: string): void
  (e: 'onStatsUpdated', stats: PlayerWebRtcStats): void
}>()

// 상태
const connectionState = ref<ConnectionState>('idle')
const errorCode = ref<PlayerErrorCode | null>(null)
const stopReason = ref<StopReason | null>(null)
const retryCount = ref(0)
const maxRetry = 8

const isLoading = computed(() =>
  ['connecting', 'joining', 'subscribing', 'buffering'].includes(connectionState.value),
)
const errorMessage = computed(() =>
  errorCode.value ? getErrorInfo(errorCode.value).message : '',
)
const canRetry = computed(() =>
  errorCode.value ? getErrorInfo(errorCode.value).canRetry : false,
)

const TAG = '[DronecastPlayer]'

function setState(state: ConnectionState) {
  console.log(`${TAG} 상태 변경: ${connectionState.value} → ${state}`)
  connectionState.value = state
  emit('onConnectionStateChanged', state)
}

function setError(code: PlayerErrorCode) {
  console.error(`${TAG} 에러 발생: ${code} — ${getErrorInfo(code).message}`)
  errorCode.value = code
  stopReason.value = 'error'
  setState('error')
}

// expose
defineExpose({
  play: (overrideCastroomId?: TDCCastroomId) => {
    if (overrideCastroomId) {
      overrideId = { gcs: overrideCastroomId.gcs, drone: overrideCastroomId.drone, camera: overrideCastroomId.camera }
    }
    startPlay()
  },
  stop: () => {
    stopViewing('user')
  },
  getConnectionState: () => connectionState.value,
})

onMounted(() => {
  if (props.autoPlay) {
    startPlay()
  }
})

// 내부 변수
const remoteVideo = ref<HTMLVideoElement | null>(null)
const detectionCanvas = ref<HTMLCanvasElement | null>(null)
let webRtcPeer: any | null = null
let iceCandidateQueue: RTCIceCandidate[] = []

// Object Detection
const detectionEnabledRef = computed(() => !!props.detectionEnabled)
useObjectDetection(remoteVideo, detectionCanvas, detectionEnabledRef)
let client: DronecastClient | null = null
let overrideId: TDCCastroomId | null = null
let mediaTimeoutTimer: ReturnType<typeof setTimeout> | null = null

function startPlay() {
  const id = overrideId || props.castroomId
  console.log(`${TAG} startPlay() — castroomId:`, id)
  errorCode.value = null
  stopReason.value = null
  retryCount.value = 0

  setState('connecting')
  initClient()
  join()
}

function initClient() {
  if (client) {
    console.log(`${TAG} initClient: 기존 클라이언트 종료`)
    client.close()
    client = null
  }
  console.log(`${TAG} initClient: wsUrl=${props.wsUrl}`)
  client = new DronecastClient('viewer')
  client.onWsStateChange = (state) => {
    console.log(`${TAG} WS 상태: ${state}`)
    emit('onWsStateChanged', state)
  }
  client.connect(props.wsUrl)
  client.setMessageHandler('join_castroom_result', onJoinCastroomResult)
  client.setMessageHandler('subscribe_stream_result', onSubscribeStreamResult)
  client.setMessageHandler('exchange_icecandidate', onExchangeIcecandidate)
  client.setMessageHandler('castroom_status_changed', onCastroomStatusChanged)
  client.setMessageHandler('caster_link_status_changed', onCasterLinkStatusChanged)
  // BL-604: 서버 push 미디어 이벤트 → monitoring store 반영
  client.setMessageHandler('evt.media.state_changed', (msg) => {
    monitoringService.handleServerMediaStateChanged(msg.payload as any)
  })
  client.setMessageHandler('evt.media.quality_report', (msg) => {
    monitoringService.handleServerMediaQualityReport(msg.payload as any)
  })
}

function join() {
  setState('joining')
  const id = overrideId || { gcs: props.castroomId?.gcs, drone: props.castroomId?.drone, camera: props.castroomId?.camera }
  console.log(`${TAG} join: castroomId=`, id)
  client?.joinCastroom(id as TDCCastroomId, 0)
}

async function onJoinCastroomResult(message: IDCMessage) {
  const { result, castroomId, data, error } = message.payload as TDCPayloadJoinCastroomResult
  console.log(`${TAG} joinCastroomResult: result=${result}, error=${error}, data=`, data)

  if (result === 'success') {
    // 서버에서 내려준 캐스트룸 정보에서 mediaServerType 추출
    if (data && typeof data === 'object') {
      // 미디어 서버는 headless 단일이다(ADR-B25). 값은 진단용으로만 찍는다.
      console.log(`${TAG} join 성공 — mediaServerType=${(data as any).mediaServerType}`)
    }
    setState('subscribing')
    startViewing()
  } else if (result === 'retry') {
    retryCount.value = (data as number) || retryCount.value + 1
    console.log(`${TAG} join 재시도 ${retryCount.value}/${maxRetry}`)
    if (retryCount.value >= maxRetry) {
      setError('JOIN_FAILED')
      return
    }
    // Caster의 미디어 세션 준비를 기다리기 위해 1초 딜레이
    setTimeout(() => {
      client?.joinCastroom(castroomId, retryCount.value)
    }, 1000)
  } else {
    console.error(`${TAG} join 실패: result=${result}, error=${error}`)
    if (error?.includes('not found') || error?.includes('not exist')) {
      setError('JOIN_REJECTED')
    } else {
      setError('JOIN_FAILED')
    }
  }
}

async function onSubscribeStreamResult(message: IDCMessage) {
  const { result, data, error } = message.payload as TDCPayloadSubscribeStreamResult
  console.log(`${TAG} subscribeStreamResult: result=${result}, hasData=${!!data}, error=${error}`)
  if (result !== 'success' || !data) {
    console.error(`${TAG} subscribe 실패: result=${result}, error=${error}`)
    setError('SDP_NEGOTIATION_FAILED')
    return
  }

  await handleHeadlessSubscribeResult(data as string)
}

/** Relay-sends-offer: data는 Relay의 SDP offer — answer 생성 후 전송 */
async function handleHeadlessSubscribeResult(offerSdp: string) {
  console.log(`${TAG} Relay Offer SDP 수신, 길이=${offerSdp.length}`)
  setState('buffering')
  startMediaTimeout()

  const pc = webRtcPeer?.peerConnection as RTCPeerConnection
  if (!pc) {
    console.error(`${TAG} PeerConnection 없음`)
    setError('SDP_NEGOTIATION_FAILED')
    return
  }

  try {
    await pc.setRemoteDescription(new RTCSessionDescription({ type: 'offer', sdp: offerSdp }))
    const answer = await pc.createAnswer()
    await pc.setLocalDescription(answer)
    console.log(`${TAG} Answer 생성 완료, 길이=${answer.sdp?.length}`)

    if (answer.sdp) {
      client?.send({
        protocol: DRONECAST_PROTOCOL,
        type: 'subscribe_stream_answer',
        payload: { castroomId: overrideId || { gcs: props.castroomId?.gcs, drone: props.castroomId?.drone, camera: props.castroomId?.camera }, sdpOffer: answer.sdp }
      })
    }

    for (const candidate of iceCandidateQueue) {
      await pc.addIceCandidate(candidate)
    }
    iceCandidateQueue = []
  } catch (err) {
    console.error(`${TAG} Relay offer 처리 에러:`, err)
    setError('SDP_NEGOTIATION_FAILED')
  }
}

function onExchangeIcecandidate(message: IDCMessage) {
  const { candidate } = message.payload as TDCPayloadExchangeIcecandidate
  const iceCandidate = candidate as RTCIceCandidate
  const hasRemoteDesc = !!webRtcPeer?.peerConnection?.remoteDescription
  console.log(`${TAG} ICE candidate 수신 (서버→클라이언트), hasRemoteDesc=${hasRemoteDesc}, candidate=${(iceCandidate as any)?.candidate?.substring(0, 60)}`)
  if (webRtcPeer && hasRemoteDesc) {
    webRtcPeer.addIceCandidate(iceCandidate).catch((err: Error) => {
      console.error(`${TAG} addIceCandidate 실패:`, err)
    })
  } else {
    iceCandidateQueue.push(iceCandidate)
    console.log(`${TAG} ICE candidate 큐잉 (총 ${iceCandidateQueue.length}개)`)
  }
}

function onCasterLinkStatusChanged(message: IDCMessage) {
  const payload = message.payload as any
  emit('onCasterLinkStatusChanged', payload.casterLinkStatus || 'unknown')
}

function onCastroomStatusChanged(message: IDCMessage) {
  const { castroomId, currentStatus, reason } = message.payload as TDCPayloadCastroomStatusChanged
  console.log(`${TAG} castroomStatusChanged: status=${currentStatus}, reason=${reason ?? '-'}`)
  emit('onCastroomStatusChanged', { castroomId, status: currentStatus, reason })

  if (currentStatus === 'stopped' || currentStatus === 'closed') {
    stopViewing('server')
  }
}

async function startViewing() {
  if (!remoteVideo.value) {
    console.error(`${TAG} startViewing: video 엘리먼트 없음`)
    setError('SDP_NEGOTIATION_FAILED')
    return
  }

  startViewingHeadless()
}

/** Relay-sends-offer: 네이티브 PeerConnection 생성, sdpOffer 없이 subscribe */
function startViewingHeadless() {
  console.log(`${TAG} startViewing: Headless Relay-sends-offer 모드`)

  const pc = new RTCPeerConnection({
    iceServers: [{ urls: 'stun:52.78.47.76:3478' }],
  })

  pc.onicecandidate = (event) => {
    if (event.candidate) {
      client?.exchangeIceCandidate(event.candidate)
    }
  }

  pc.ontrack = (event) => {
    console.log(`${TAG} ontrack: kind=${event.track.kind}, streams=${event.streams.length}`)
    if (!remoteVideo.value) return

    const stream = event.streams[0] || new MediaStream([event.track])
    remoteVideo.value.srcObject = stream
    remoteVideo.value.play().catch((err) => {
      console.warn(`${TAG} video.play() 실패 (autoplay 정책):`, err.message)
    })
  }

  setupPeerConnectionEvents(pc)

  webRtcPeer = {
    peerConnection: pc,
    processAnswer: async () => {},
    addIceCandidate: async (candidate: RTCIceCandidate) => { await pc.addIceCandidate(candidate) },
    generateOffer: () => '',
  }

  // Relay에 receiver 생성 요청 (sdpOffer 없이)
  client?.subscribeStream('')

  remoteVideo.value!.removeEventListener('playing', onVideoPlaying)
  remoteVideo.value!.addEventListener('playing', onVideoPlaying)
  startStatsPoll()
}

/** PeerConnection 이벤트 핸들러 설정 */
function setupPeerConnectionEvents(pc: RTCPeerConnection) {
  pc.onconnectionstatechange = () => {
    const state = pc.connectionState
    console.log(`${TAG} PC connectionState: ${state}`)
    emit('onWebRtcStateChanged', 'viewer', state, undefined)
    if (state === 'connected') console.log(`${TAG} WebRTC 연결 성공!`)
    else if (state === 'failed') { console.error(`${TAG} WebRTC 연결 실패`); setError('ICE_CONNECTION_FAILED') }
  }

  pc.oniceconnectionstatechange = () => {
    console.log(`${TAG} ICE connectionState: ${pc.iceConnectionState}`)
    emit('onWebRtcStateChanged', 'viewer', undefined, pc.iceConnectionState)
  }
}

/** WebRTC stats polling — 이전 스냅샷 보관용 */
let prevStatsSnapshot: {
  timestamp: number
  bytesReceived: number
  packetsReceived: number
  packetsLost: number
  framesDecoded: number
  framesDropped: number
  totalDecodeTime: number
  totalProcessingDelay: number
  nackCount: number
  pliCount: number
} | null = null
let statsTimer: ReturnType<typeof setInterval> | null = null

function startStatsPoll() {
  prevStatsSnapshot = null
  statsTimer = setInterval(async () => {
    if (!webRtcPeer?.peerConnection) { stopStatsPoll(); return }
    try {
      const peerConn = webRtcPeer.peerConnection as RTCPeerConnection
      const rawStats = await peerConn.getStats()
      const result = createEmptyStats()

      let inbound: any = null
      let candidatePair: any = null
      let remoteCandidate: any = null
      let codecReport: any = null

      rawStats.forEach((report: any) => {
        if (report.type === 'inbound-rtp' && report.kind === 'video') inbound = report
        if (report.type === 'candidate-pair' && report.state === 'succeeded') candidatePair = report
        if (report.type === 'remote-candidate') remoteCandidate = report
        if (report.type === 'codec' && report.mimeType?.startsWith('video/')) codecReport = report
      })

      // 아직 remote-candidate를 못 찾았으면 candidatePair.remoteCandidateId로 재탐색
      if (!remoteCandidate && candidatePair?.remoteCandidateId) {
        remoteCandidate = rawStats.get(candidatePair.remoteCandidateId)
      }
      // codec도 inbound의 codecId로 재탐색
      if (!codecReport && inbound?.codecId) {
        codecReport = rawStats.get(inbound.codecId)
      }

      const now = Date.now()

      // ── inbound-rtp 기반 ──
      if (inbound) {
        // 버퍼링 상태에서 프레임 디코딩 시작 감지
        if (inbound.framesDecoded > 0 && connectionState.value === 'buffering') {
          console.log(`${TAG} 프레임 디코딩 시작 → 타임아웃 해제`)
          clearMediaTimeout()
        }

        result.fps = inbound.framesPerSecond ?? null
        result.resolution = (inbound.frameWidth && inbound.frameHeight)
          ? `${inbound.frameWidth}×${inbound.frameHeight}` : null
        result.jitter = inbound.jitter != null ? Math.round(inbound.jitter * 1000) : null
        result.decoderImpl = inbound.decoderImplementation ?? null

        // jitter buffer delay
        if (inbound.jitterBufferDelay != null && inbound.jitterBufferEmittedCount > 0) {
          result.jitterBufferDelay = Math.round(
            (inbound.jitterBufferDelay / inbound.jitterBufferEmittedCount) * 1000,
          )
        }

        // 차분 계산 (이전 스냅샷 필요)
        if (prevStatsSnapshot) {
          const dt = (now - prevStatsSnapshot.timestamp) / 1000
          if (dt > 0) {
            // bitrate
            const bytesDiff = (inbound.bytesReceived ?? 0) - prevStatsSnapshot.bytesReceived
            result.bitrate = Math.round((bytesDiff * 8) / dt / 1000)

            // packet loss rate
            const lostDiff = (inbound.packetsLost ?? 0) - prevStatsSnapshot.packetsLost
            const recvDiff = (inbound.packetsReceived ?? 0) - prevStatsSnapshot.packetsReceived
            const totalDiff = lostDiff + recvDiff
            result.packetLossRate = totalDiff > 0 ? Math.round((lostDiff / totalDiff) * 1000) / 10 : 0

            // decode time (ms/frame)
            const framesDiff = (inbound.framesDecoded ?? 0) - prevStatsSnapshot.framesDecoded
            if (framesDiff > 0) {
              const decodeDiff = ((inbound.totalDecodeTime ?? 0) - prevStatsSnapshot.totalDecodeTime)
              result.decodeTime = Math.round((decodeDiff / framesDiff) * 10000) / 10

              const procDiff = ((inbound.totalProcessingDelay ?? 0) - prevStatsSnapshot.totalProcessingDelay)
              result.processingDelay = Math.round((procDiff / framesDiff) * 10000) / 10
            }

            // frames dropped
            result.framesDropped = (inbound.framesDropped ?? 0) - prevStatsSnapshot.framesDropped

            // nack / pli per sec
            result.nackPerSec = Math.round(((inbound.nackCount ?? 0) - prevStatsSnapshot.nackCount) / dt * 10) / 10
            result.pliPerSec = Math.round(((inbound.pliCount ?? 0) - prevStatsSnapshot.pliCount) / dt * 10) / 10
          }
        }

        // 스냅샷 갱신
        prevStatsSnapshot = {
          timestamp: now,
          bytesReceived: inbound.bytesReceived ?? 0,
          packetsReceived: inbound.packetsReceived ?? 0,
          packetsLost: inbound.packetsLost ?? 0,
          framesDecoded: inbound.framesDecoded ?? 0,
          framesDropped: inbound.framesDropped ?? 0,
          totalDecodeTime: inbound.totalDecodeTime ?? 0,
          totalProcessingDelay: inbound.totalProcessingDelay ?? 0,
          nackCount: inbound.nackCount ?? 0,
          pliCount: inbound.pliCount ?? 0,
        }
      }

      // ── candidate-pair 기반 ──
      if (candidatePair) {
        result.rtt = candidatePair.currentRoundTripTime != null
          ? Math.round(candidatePair.currentRoundTripTime * 1000) : null
        result.availableBandwidth = candidatePair.availableIncomingBitrate != null
          ? Math.round(candidatePair.availableIncomingBitrate / 1000) : null
      }

      // ── remote-candidate 기반 ──
      if (remoteCandidate) {
        result.candidateType = remoteCandidate.candidateType ?? null
        result.protocol = remoteCandidate.protocol ?? null
      }

      // ── codec ──
      if (codecReport) {
        result.codec = codecReport.mimeType?.replace('video/', '') ?? null
      }

      emit('onStatsUpdated', result)
    } catch (err) { stopStatsPoll() }
  }, 2000)
}

function stopStatsPoll() {
  if (statsTimer) { clearInterval(statsTimer); statsTimer = null }
  prevStatsSnapshot = null
}

function onVideoPlaying() {
  console.log(`${TAG} video 'playing' 이벤트 발생! → 재생 시작`)
  clearMediaTimeout()
  setState('playing')
}

function startMediaTimeout() {
  clearMediaTimeout()
  mediaTimeoutTimer = setTimeout(() => {
    if (connectionState.value === 'buffering') {
      setError('MEDIA_TIMEOUT')
    }
  }, 15000)
}

function clearMediaTimeout() {
  if (mediaTimeoutTimer) {
    clearTimeout(mediaTimeoutTimer)
    mediaTimeoutTimer = null
  }
}

function stopViewing(reason: StopReason) {
  console.log(`${TAG} stopViewing: reason=${reason}`)
  clearMediaTimeout()
  stopStatsPoll()
  stopReason.value = reason

  try {
    if (webRtcPeer) {
      const pc = webRtcPeer.peerConnection as RTCPeerConnection | undefined
      if (pc) pc.close()
      webRtcPeer = null
    }
    iceCandidateQueue = []
  } catch (err) {
    console.error(`${TAG} stopViewing cleanup 에러:`, err)
  }

  // video srcObject 해제
  if (remoteVideo.value) {
    remoteVideo.value.srcObject = null
  }

  if (reason === 'user') {
    setState('stopped')
  } else if (reason === 'server') {
    errorCode.value = 'CASTROOM_CLOSED'
    setState('stopped')
  }
}

function retry() {
  startPlay()
}

onUnmounted(() => {
  console.log(`${TAG} onUnmounted: cleanup`)
  clearMediaTimeout()
  stopStatsPoll()
  try {
    if (webRtcPeer) {
      const pc = webRtcPeer.peerConnection as RTCPeerConnection | undefined
      if (pc) pc.close()
      webRtcPeer = null
    }
    if (remoteVideo.value) {
      remoteVideo.value.removeEventListener('playing', onVideoPlaying)
      remoteVideo.value.srcObject = null
    }
    client?.close()
    client = null
    iceCandidateQueue = []
  } catch (err) {
    console.error(`${TAG} onUnmounted cleanup 에러:`, err)
  }
})
</script>

<style scoped>
.dronecast-player {
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
}

.remote-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

.detection-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.7);
}

.overlay-content {
  text-align: center;
  color: #fff;
}

.loading-icon {
  width: 80px;
  height: 80px;
  margin-bottom: 16px;
}

.status-text {
  font-size: 14px;
  color: #ccc;
  margin: 0;
}

.error-message {
  font-size: 16px;
  color: #ff6b6b;
  margin: 0 0 16px 0;
}

.retry-button {
  padding: 8px 24px;
  background: #4a90d9;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.retry-button:hover {
  background: #357abd;
}

.stopped-message,
.idle-message {
  font-size: 16px;
  color: #aaa;
  margin: 0;
}

/* compact 모드 (미리보기 등 소형 영역) */
.compact .loading-icon {
  width: 24px;
  height: 24px;
  margin-bottom: 4px;
}

.compact .status-text {
  font-size: 10px;
}

.compact .error-message {
  font-size: 10px;
  margin: 0 0 4px 0;
}

.compact .retry-button {
  padding: 2px 8px;
  font-size: 10px;
  border-radius: 3px;
}

.compact .stopped-message,
.compact .idle-message {
  font-size: 10px;
}
</style>
